/**
 * Background Sync — periodically syncs upcoming reminders for local notifications.
 *
 * Uses expo-background-task to run a periodic task that:
 * 1. Fetches upcoming reminders from the server
 * 2. Schedules local notifications as offline fallback
 *
 * Also uses expo-task-manager for task registration.
 */

import * as BackgroundTask from 'expo-background-task';
import * as TaskManager from 'expo-task-manager';
import { Platform } from 'react-native';
import { useRemindersStore } from '../stores/reminderStore';

const BACKGROUND_SYNC_TASK = 'FISHMEMORY_REMINDER_SYNC';

// Define the background task (native only — TaskManager crashes on web)
if (Platform.OS !== 'web') {
  TaskManager.defineTask(BACKGROUND_SYNC_TASK, async () => {
    try {
      await useRemindersStore.getState().syncFromServer();
      return BackgroundTask.BackgroundTaskResult.Success;
    } catch {
      useRemindersStore.getState().rescheduleFromLocal();
      return BackgroundTask.BackgroundTaskResult.Failed;
    }
  });
}

/**
 * Register the background task.
 * Call once after authentication.
 */
export async function registerBackgroundSync(): Promise<void> {
  if (Platform.OS === 'web') return;
  try {
    const status = await BackgroundTask.getStatusAsync();
    if (status === BackgroundTask.BackgroundTaskStatus.Restricted) {
      console.warn('[BgSync] Background task restricted by OS');
      return;
    }

    const isRegistered = await TaskManager.isTaskRegisteredAsync(BACKGROUND_SYNC_TASK);
    if (!isRegistered) {
      await BackgroundTask.registerTaskAsync(BACKGROUND_SYNC_TASK, {
        minimumInterval: 15 * 60, // 15 minutes
      });
      console.log('[BgSync] Background sync registered');
    }
  } catch (err) {
    console.warn('[BgSync] Failed to register:', err);
  }
}

